import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useNotify } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, money, formatDate, PartnerInvoice, InvoiceStatus } from './api';
import { UploadInvoiceDialog } from './UploadInvoiceDialog';
import { PartnerMonthsGrid } from './PartnerMonthsGrid';

const STATUS_COLOR: Record<InvoiceStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  extracting: 'info',
  review: 'warning',
  reconciled: 'info',
  paid: 'success',
  failed: 'error',
};

// Some partners bill by period and leave the single invoice date blank
// (À Votre Service). Fall back to the period so the Date column isn't empty.
const effectiveDate = (inv: PartnerInvoice): string | null =>
  inv.invoice_date || inv.period_end || inv.period_start || null;

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  extracting: 'Extracting',
  review: 'Needs review',
  reconciled: 'Reconciled',
  paid: 'Paid',
  failed: 'Failed',
};

export const PartnerReconciliationList = () => {
  const [invoices, setInvoices] = useState<PartnerInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sortField, setSortField] = useState<'invoice_date' | 'created_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const notify = useNotify();
  const navigate = useNavigate();
  // T-082: "By partner & month" is the first tab; the invoice list is one tab over.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'months' | 'list' = searchParams.get('view') === 'list' ? 'list' : 'months';

  const toggleSort = (field: 'invoice_date' | 'created_at') => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedInvoices = useMemo(() => {
    const arr = [...invoices];
    arr.sort((a, b) => {
      const av = sortField === 'invoice_date' ? effectiveDate(a) : a[sortField];
      const bv = sortField === 'invoice_date' ? effectiveDate(b) : b[sortField];
      if (!av && !bv) return 0;
      if (!av) return 1; // nulls last
      if (!bv) return -1;
      const cmp = new Date(av).getTime() - new Date(bv).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [invoices, sortField, sortDir]);

  const fetchInvoices = useCallback(async () => {
    try {
      const data = await apiFetch('partner-invoices');
      setInvoices(data || []);
    } catch {
      notify('Failed to load invoices', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5">Partner Invoice Reconciliation</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}>
          Upload invoice
        </Button>
      </Box>

      <Tabs
        value={view}
        onChange={(_e, v) => setSearchParams({ view: v }, { replace: true })}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="months" label="By partner & month" />
        <Tab value="list" label="All uploaded invoices" />
      </Tabs>

      {view === 'months' && <PartnerMonthsGrid />}

      {view === 'list' && (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell sortDirection={sortField === 'invoice_date' ? sortDir : false}>
                <TableSortLabel
                  active={sortField === 'invoice_date'}
                  direction={sortField === 'invoice_date' ? sortDir : 'desc'}
                  onClick={() => toggleSort('invoice_date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Cases</TableCell>
              <TableCell align="center">Matched</TableCell>
              <TableCell align="center">Reconciled</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sortDirection={sortField === 'created_at' ? sortDir : false}>
                <TableSortLabel
                  active={sortField === 'created_at'}
                  direction={sortField === 'created_at' ? sortDir : 'desc'}
                  onClick={() => toggleSort('created_at')}
                >
                  Uploaded
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No invoices yet. Click "Upload invoice" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedInvoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  hover
                  onClick={() => navigate(`/partner-reconciliation/${inv.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>{inv.partner?.name || '—'}</TableCell>
                  <TableCell>
                    {inv.partner_type === 'crematorium' ? (
                      <WhatshotIcon fontSize="small" color="action" titleAccess="Crematorium" />
                    ) : inv.partner_type === 'dropoff_location' ? (
                      <WarehouseIcon fontSize="small" color="action" titleAccess="Drop-off facility" />
                    ) : (
                      <LocalShippingIcon fontSize="small" color="action" titleAccess="Transport" />
                    )}
                  </TableCell>
                  <TableCell>{inv.invoice_number || '—'}</TableCell>
                  <TableCell>{formatDate(effectiveDate(inv))}</TableCell>
                  <TableCell align="right">
                    {/* invoice_total is the document's own total whenever it
                        reconciles against the case lines — tax-inclusive or not.
                        It falls back to the case sum only when the two genuinely
                        disagree, which is how a mis-extracted batch header gets
                        noticed instead of quietly passing. */}
                    {money(inv.invoice_total ?? inv.cases_total_sum ?? inv.extracted_total, inv.currency)}
                    {inv.total_matches_extracted === false && (
                      <Tooltip
                        title={`Document states ${money(inv.extracted_total, inv.currency)}, but its case lines add up to ${money(
                          inv.cases_total_sum,
                          inv.currency,
                        )} and that gap is not the invoice tax. Showing the case total.`}
                      >
                        <Typography variant="caption" color="warning.main" display="block" sx={{ cursor: 'help' }}>
                          doc says {money(inv.extracted_total, inv.currency)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">{inv.case_count ?? 0}</TableCell>
                  <TableCell align="center">
                    {inv.matched_count ?? 0}/{inv.case_count ?? 0}
                  </TableCell>
                  <TableCell align="center">
                    {inv.reconciled_count ?? 0}/{inv.case_count ?? 0}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABEL[inv.status]}
                      color={STATUS_COLOR[inv.status]}
                      variant={inv.status === 'paid' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(inv.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <UploadInvoiceDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(id) => {
          void fetchInvoices();
          navigate(`/partner-reconciliation/${id}`);
        }}
      />
    </Box>
  );
};

export default PartnerReconciliationList;
